import React, { useState } from 'react';
import { Button } from './Button';
import { Select } from './Select';
import { Input } from './Input';
import { Badge } from './Badge';
import { cn } from '../../utils/cn';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { BulkOperation, BulkOperationResult } from '../../types';

interface BulkActionBarProps {
  selectedCount: number;
  onClear: () => void;
  onExecute: (operation: BulkOperation) => Promise<BulkOperationResult>;
  loading?: boolean;
  className?: string;
  entityLabel?: string;
  customActionOptions?: { label: string; value: string }[];
  customStatusOptions?: { label: string; value: string }[];
}

interface ProgressState {
  isRunning: boolean;
  current: number;
  total: number;
  completed: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
}

const actionOptions = [
  { label: 'Select action...', value: '' },
  { label: 'Change Status', value: 'status' },
  { label: 'Change Plan', value: 'plan' },
  { label: 'Adjust Credits', value: 'credits' },
  { label: 'Delete Users', value: 'delete' },
];

const statusOptions = [
  { label: 'Select status...', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Banned', value: 'banned' },
];

const planOptions = [
  { label: 'Select plan...', value: '' },
  { label: 'Free', value: 'free' },
  { label: 'Starter', value: 'starter' },
  { label: 'Creator', value: 'creator' },
  { label: 'Pro', value: 'pro' },
  { label: 'Studio', value: 'studio' },
];

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onClear,
  onExecute,
  loading = false,
  className,
  entityLabel = 'user',
  customActionOptions,
  customStatusOptions,
}) => {
  const resolvedActionOptions = customActionOptions ?? actionOptions;
  const resolvedStatusOptions = customStatusOptions ?? statusOptions;
  const [action, setAction] = useState('');
  const [value, setValue] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditType, setCreditType] = useState<'grant' | 'deduct' | 'set'>(
    'grant'
  );
  const [progress, setProgress] = useState<ProgressState>({
    isRunning: false,
    current: 0,
    total: 0,
    completed: 0,
    failed: 0,
    errors: [],
  });

  const handleExecute = async () => {
    if (!action) return;

    let operationValue: any = value;

    if (action === 'credits') {
      const amount = parseInt(creditAmount);
      if (isNaN(amount) || amount <= 0) return;
      operationValue = { type: creditType, amount };
    }

    setProgress({
      isRunning: true,
      current: 0,
      total: selectedCount,
      completed: 0,
      failed: 0,
      errors: [],
    });

    try {
      const result = await onExecute({
        type: action as any,
        value: operationValue,
        selectedIds: [], // This will be filled by the parent component
      });

      setProgress((prev) => ({
        ...prev,
        isRunning: false,
        current: prev.total,
        completed: result.success,
        failed: result.failed,
        errors: result.errors,
      }));

      if (result.failed === 0) {
        setTimeout(() => {
          setAction('');
          setValue('');
          setCreditAmount('');
          setCreditType('grant');
          setProgress({
            isRunning: false,
            current: 0,
            total: 0,
            completed: 0,
            failed: 0,
            errors: [],
          });
        }, 3000);
      }
    } catch (error) {
      setProgress((prev) => ({
        ...prev,
        isRunning: false,
        failed: prev.total,
        errors: [
          {
            id: 'bulk-error',
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        ],
      }));
    }
  };

  const canExecute = () => {
    if (!action || selectedCount === 0) return false;

    switch (action) {
      case 'status':
      case 'plan':
        return !!value;
      case 'credits':
        return !!creditAmount && !isNaN(parseInt(creditAmount));
      case 'delete':
        return true;
      default:
        return false;
    }
  };

  if (selectedCount === 0 && !progress.isRunning && progress.total === 0)
    return null;

  return (
    <div
      className={cn(
        'bg-primary-900/20 border border-primary-600 rounded-lg p-4 space-y-4',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Badge variant="info" className="px-3 py-1">
            {selectedCount} {entityLabel}
            {selectedCount !== 1 ? 's' : ''} selected
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={loading || progress.isRunning}
          >
            Clear Selection
          </Button>
        </div>
      </div>

      {/* Progress Tracking */}
      {(progress.isRunning || progress.total > 0) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">
              {progress.isRunning ? 'Processing...' : 'Operation Complete'}
            </span>
            <span className="text-sm text-gray-400">
              {progress.current} / {progress.total}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                progress.isRunning
                  ? 'bg-primary-500'
                  : progress.failed > 0
                    ? 'bg-error-500'
                    : 'bg-success-500'
              )}
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>

          {/* Results Summary */}
          {!progress.isRunning && progress.total > 0 && (
            <div className="flex items-center space-x-4 text-sm">
              {progress.completed > 0 && (
                <div className="flex items-center space-x-1 text-success-400">
                  <CheckCircleIcon className="h-4 w-4" />
                  <span>{progress.completed} successful</span>
                </div>
              )}
              {progress.failed > 0 && (
                <div className="flex items-center space-x-1 text-error-400">
                  <XCircleIcon className="h-4 w-4" />
                  <span>{progress.failed} failed</span>
                </div>
              )}
            </div>
          )}

          {/* Error Details */}
          {progress.errors.length > 0 && (
            <div className="bg-error-900/20 border border-error-600 rounded p-3 space-y-2">
              <div className="flex items-center space-x-2 text-error-400">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="font-medium">Errors occurred:</span>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {progress.errors.map((error, index) => (
                  <div key={index} className="text-sm text-gray-300">
                    <span className="font-mono text-xs text-gray-400">
                      {error.id}:
                    </span>{' '}
                    {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <Select
          label="Action"
          options={resolvedActionOptions}
          value={action}
          onChange={setAction}
          disabled={loading}
        />

        {action === 'status' && (
          <Select
            label="New Status"
            options={resolvedStatusOptions}
            value={value}
            onChange={setValue}
            disabled={loading}
          />
        )}

        {action === 'plan' && (
          <Select
            label="New Plan"
            options={planOptions}
            value={value}
            onChange={setValue}
            disabled={loading}
          />
        )}

        {action === 'credits' && (
          <>
            <Select
              label="Credit Action"
              options={[
                { label: 'Grant (add)', value: 'grant' },
                { label: 'Deduct (subtract)', value: 'deduct' },
                { label: 'Set (override)', value: 'set' },
              ]}
              value={creditType}
              onChange={(val) => setCreditType(val as any)}
              disabled={loading}
            />
            <Input
              label="Amount"
              type="number"
              min="1"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="Enter amount"
              disabled={loading}
            />
          </>
        )}

        {action === 'delete' && (
          <div className="text-sm text-error-400 bg-error-900/20 border border-error-600 rounded p-2">
            ⚠️ This will permanently delete selected {entityLabel}s
          </div>
        )}

        <Button
          onClick={handleExecute}
          disabled={!canExecute() || loading || progress.isRunning}
          variant={action === 'delete' ? 'destructive' : 'default'}
          className="w-full"
        >
          {progress.isRunning
            ? 'Processing...'
            : loading
              ? 'Loading...'
              : `Apply to ${selectedCount} ${entityLabel}${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
};
